import { type FC, useMemo } from 'react';

import { Button, Flex, HStack, Text } from '@chakra-ui/react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from '@/components/Form';
import { IconEmptyStar, IconFilledStar } from '@/components/Icons';

export const ratingItems = ['1', '2', '3', '4', '5'] as const;

export type RatingObj = Record<(typeof ratingItems)[number], boolean>;

export type FormInputs = RatingObj;

type RatingFilterComponentProps = {
  ratingDefaultValues: RatingObj;
  onSubmit: (newRating: RatingObj) => void;
  onclose: () => void;
};

export const RatingFilterComponent: FC<RatingFilterComponentProps> = ({
  ratingDefaultValues,
  onSubmit,
  onclose,
}) => {
  const { t } = useTranslation(['common', 'components']);

  const form = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: ratingDefaultValues,
  });

  const onFormSubmit: SubmitHandler<FormInputs> = async (data) => {
    onSubmit(data);
    onclose();
  };

  const starElements = useMemo(() => {
    return ratingItems.map((n, idx) => {
      const numberOfFilledStars = idx + 1;
      const numberOfEmptyStars = ratingItems.length - numberOfFilledStars;

      return (
        <HStack key={n}>
          {[...Array(numberOfFilledStars).keys()].map((n) => (
            <IconFilledStar key={n} fontSize="larger" />
          ))}
          {[...Array(numberOfEmptyStars).keys()].map((n) => (
            <IconEmptyStar key={n} fontSize="larger" />
          ))}
        </HStack>
      );
    });
  }, []);

  return (
    <Flex flexDir="column" p={3}>
      <Text size="sm" fontWeight="medium" color="gray.700" mb={4}>
        {t('components:dashboard.filters.rating')}
      </Text>

      <Form {...form} onSubmit={onFormSubmit}>
        <Flex flexDir="column" rowGap={4}>
          {ratingItems.map((item, idx) => {
            return (
              <FormField
                key={item}
                name={item}
                control={form.control}
                type="checkbox"
                size="lg"
              >
                {starElements[idx]}
              </FormField>
            );
          })}
        </Flex>

        <Button type="submit" mt={4} variant="@primary" size="sm" w="full">
          {t('common:actions.applyFilter')}
        </Button>
      </Form>
    </Flex>
  );
};
